import { useCallback } from 'react';

import { gql } from '@apollo/client';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { TaskSubgroup } from '@this+that/models';

import { buildAbortableQueryContext } from './buildAbortableQueryContext';

const TASK_SUBGROUP_FIELDS = gql`
  fragment TaskSubgroupFields on TaskSubgroup {
    taskGroupId
    id
    title
    orderKey
    createdFor
    createdAt
    updatedAt
  }
`;

const GET_TASK_SUBGROUPS = gql`
  query getTaskSubgroups($taskGroupId: String!) {
    getTaskSubgroups(taskGroupId: $taskGroupId) {
      ...TaskSubgroupFields
    }
  }
  ${TASK_SUBGROUP_FIELDS}
`;

interface GetTaskSubgroupsParams {
  taskGroupId: string;
  signal?: AbortSignal;
}

interface GetTaskSubgroupsData {
  getTaskSubgroups: TaskSubgroup[];
}

interface CreateTaskSubgroupData {
  createTaskSubgroup: TaskSubgroup;
}

interface UpdateTaskSubgroupData {
  updateTaskSubgroup: TaskSubgroup;
}

interface DeleteTaskSubgroupData {
  deleteTaskSubgroup: boolean;
}

export function useGetTaskSubgroups() {
  const [runQuery, { loading }] = useLazyQuery<GetTaskSubgroupsData>(
    GET_TASK_SUBGROUPS,
    {
      fetchPolicy: 'network-only',
    },
  );

  const getTaskSubgroups = useCallback(
    async ({ taskGroupId, signal }: GetTaskSubgroupsParams) => {
      const { data } = await runQuery({
        variables: { taskGroupId },
        context: buildAbortableQueryContext(signal),
      });

      return data?.getTaskSubgroups ?? [];
    },
    [runQuery],
  );

  return { getTaskSubgroups, loading };
}

const CREATE_TASK_SUBGROUP = gql`
  mutation createTaskSubgroup($taskSubgroup: CreateTaskSubgroupInput!) {
    createTaskSubgroup(taskSubgroup: $taskSubgroup) {
      ...TaskSubgroupFields
    }
  }
  ${TASK_SUBGROUP_FIELDS}
`;

export function useCreateTaskSubgroup() {
  const [createTaskSubgroupMutation, { loading, error }] =
    useMutation<CreateTaskSubgroupData>(CREATE_TASK_SUBGROUP);

  const createTaskSubgroup = useCallback(
    async (taskSubgroup: Partial<TaskSubgroup>) => {
      const { data } = await createTaskSubgroupMutation({
        variables: { taskSubgroup },
      });

      return data?.createTaskSubgroup;
    },
    [createTaskSubgroupMutation],
  );

  return { createTaskSubgroup, loading, error };
}

const UPDATE_TASK_SUBGROUP = gql`
  mutation updateTaskSubgroup($taskSubgroup: UpdateTaskSubgroupInput!) {
    updateTaskSubgroup(taskSubgroup: $taskSubgroup) {
      ...TaskSubgroupFields
    }
  }
  ${TASK_SUBGROUP_FIELDS}
`;

export function useUpdateTaskSubgroup() {
  const [updateTaskSubgroupMutation, { loading, error }] =
    useMutation<UpdateTaskSubgroupData>(UPDATE_TASK_SUBGROUP);

  const updateTaskSubgroup = useCallback(
    async (taskSubgroup: Partial<TaskSubgroup>) => {
      const { data } = await updateTaskSubgroupMutation({
        variables: { taskSubgroup },
      });

      return data?.updateTaskSubgroup;
    },
    [updateTaskSubgroupMutation],
  );

  return { updateTaskSubgroup, loading, error };
}

const DELETE_TASK_SUBGROUP = gql`
  mutation deleteTaskSubgroup($taskGroupId: String!, $taskSubgroupId: String!) {
    deleteTaskSubgroup(taskGroupId: $taskGroupId, taskSubgroupId: $taskSubgroupId)
  }
`;

export function useDeleteTaskSubgroup() {
  const [deleteTaskSubgroupMutation, { loading, error }] =
    useMutation<DeleteTaskSubgroupData>(DELETE_TASK_SUBGROUP);

  const deleteTaskSubgroup = useCallback(
    async (taskGroupId: string, taskSubgroupId: string) => {
      const { data } = await deleteTaskSubgroupMutation({
        variables: { taskGroupId, taskSubgroupId },
      });

      return data?.deleteTaskSubgroup ?? false;
    },
    [deleteTaskSubgroupMutation],
  );

  return { deleteTaskSubgroup, loading, error };
}
